/*
 * Copyright 2017 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the terms of the LICENSE file distributed with this project.
 */
import * as tslib_1 from "tslib";
import * as classNames from "classnames";
import * as PureRender from "pure-render-decorator";
import * as React from "react";
import { AbstractComponent, Button, Classes as CoreClasses, MenuItem, Utils, } from "@blueprintjs/core";
import * as Classes from "../../common/classes";
import { Select } from "../select/select";
import { formatTimezone, TimezoneDisplayFormat } from "./timezoneDisplayFormat";
import { getInitialTimezoneItems, getTimezoneItems } from "./timezoneItems";
import { filterWithQueryCandidates, getTimezoneQueryCandidates } from "./timezoneUtils";
export { TimezoneDisplayFormat };
var TypedSelect = Select.ofType();
var TimezonePicker = (function (_super) {
    tslib_1.__extends(TimezonePicker, _super);
    function TimezonePicker(props, context) {
        var _this = _super.call(this, props, context) || this;
        _this.filterItems = function (query, items) {
            if (query === "") {
                return items;
            }
            var date = _this.state.date;
            return filterWithQueryCandidates(items, query, function (item) { return getTimezoneQueryCandidates(item.timezone, date); });
        };
        _this.renderItem = function (itemProps) {
            var item = itemProps.item, isActive = itemProps.isActive, handleClick = itemProps.handleClick;
            var classes = classNames(CoreClasses.MENU_ITEM, CoreClasses.intentClass(), (_a = {},
                _a[CoreClasses.ACTIVE] = isActive,
                _a[CoreClasses.INTENT_PRIMARY] = isActive,
                _a));
            return (React.createElement(MenuItem, { key: item.key, className: classes, iconName: item.iconName, text: item.text, label: item.label, onClick: handleClick, shouldDismissPopover: false }));
            var _a;
        };
        _this.handleItemSelect = function (timezone) {
            if (_this.props.value === undefined) {
                _this.setState({ value: timezone.timezone });
            }
            Utils.safeInvoke(_this.props.onChange, timezone.timezone);
        };
        _this.handleQueryChange = function (query) {
            _this.setState({ query: query });
        };
        var value = props.value, _a = props.date, date = _a === void 0 ? new Date() : _a, showLocalTimezone = props.showLocalTimezone, _b = props.inputProps, inputProps = _b === void 0 ? {} : _b;
        var query = inputProps.value !== undefined ? inputProps.value : "";
        _this.state = { date: date, value: value, query: query };
        _this.timezoneItems = getTimezoneItems(date);
        _this.initialTimezoneItems = getInitialTimezoneItems(date, showLocalTimezone);
        return _this;
    }
    TimezonePicker.prototype.render = function () {
        var _a = this.props, className = _a.className, disabled = _a.disabled, inputProps = _a.inputProps, popoverProps = _a.popoverProps;
        var query = this.state.query;
        var finalInputProps = tslib_1.__assign({ placeholder: "Search for timezones..." }, inputProps);
        var finalPopoverProps = tslib_1.__assign({}, popoverProps, { popoverClassName: classNames(Classes.TIMEZONE_PICKER_POPOVER, popoverProps.popoverClassName) });
        return (React.createElement(TypedSelect, { className: classNames(Classes.TIMEZONE_PICKER, className), items: query ? this.timezoneItems : this.initialTimezoneItems, itemListPredicate: this.filterItems, itemRenderer: this.renderItem, noResults: React.createElement(MenuItem, { disabled: true, text: "No matching timezones." }), onItemSelect: this.handleItemSelect, resetOnSelect: true, resetOnClose: true, popoverProps: finalPopoverProps, inputProps: finalInputProps, disabled: disabled, onQueryChange: this.handleQueryChange }, this.renderButton()));
    };
    TimezonePicker.prototype.componentWillReceiveProps = function (nextProps) {
        var _a = nextProps.date, nextDate = _a === void 0 ? new Date() : _a, _b = nextProps.inputProps, nextInputProps = _b === void 0 ? {} : _b;
        var dateChanged = this.state.date.getTime() !== nextDate.getTime();
        if (dateChanged) {
            this.timezoneItems = getTimezoneItems(nextDate);
        }
        if (dateChanged || this.props.showLocalTimezone !== nextProps.showLocalTimezone) {
            this.initialTimezoneItems = getInitialTimezoneItems(nextDate, nextProps.showLocalTimezone);
        }
        var nextState = {};
        if (dateChanged) {
            nextState.date = nextDate;
        }
        if (this.state.value !== nextProps.value) {
            nextState.value = nextProps.value;
        }
        if (nextInputProps.value !== undefined && this.state.query !== nextInputProps.value) {
            nextState.query = nextInputProps.value;
        }
        this.setState(nextState);
    };
    TimezonePicker.prototype.renderButton = function () {
        var _a = this.props, disabled = _a.disabled, _b = _a.valueDisplayFormat, valueDisplayFormat = _b === void 0 ? TimezoneDisplayFormat.OFFSET : _b, defaultValue = _a.defaultValue, placeholder = _a.placeholder, _c = _a.buttonProps, buttonProps = _c === void 0 ? {} : _c;
        var _d = this.state, date = _d.date, value = _d.value;
        var finalValue = value ? value : defaultValue;
        var displayValue = finalValue ? formatTimezone(finalValue, date, valueDisplayFormat) : undefined;
        return (React.createElement(Button, tslib_1.__assign({ rightIconName: "caret-down", disabled: disabled, text: displayValue || placeholder }, buttonProps)));
    };
    TimezonePicker.displayName = "Blueprint.TimezonePicker";
    TimezonePicker.defaultProps = {
        disabled: false,
        inputProps: {},
        placeholder: "Select timezone...",
        popoverProps: {},
        showLocalTimezone: true,
    };
    TimezonePicker = tslib_1.__decorate([
        PureRender
    ], TimezonePicker);
    return TimezonePicker;
}(AbstractComponent));
export { TimezonePicker };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@blueprintjs/labs/dist/esm/components/timezone-picker/timezonePicker.js
// module id = 1018
// module chunks = 0